/**
 * Iter108 — Master Console: Templates Library CRUD.
 *
 * Authors curated reusable subjects / bodies / image URLs.
 * The library is consumed by Sunbiz, Prospecting and Campaigns via
 * GET /api/library/templates?kind=&category=&language=
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Plus, Search, Trash2, Edit3, X, Save, BookOpen, Mail, Image as ImageIcon, Type,
} from "lucide-react";
import MasterLayout from "./MasterLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KINDS = [
  { id: "email_subject", label: "Subject", icon: Type },
  { id: "email_body",    label: "Body",    icon: Mail },
  { id: "image",         label: "Image",   icon: ImageIcon },
];
const LANGS = ["pt", "en", "es"];
const EMPTY = { name: "", kind: "email_body", category: "", language: "pt", content: "", tags: "" };

export default function MasterTemplates() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [search, setSearch] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/library/templates`);
      setItems(data?.items || []);
    } catch (e) {
      toast.error("Falha ao carregar templates");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const seed = async () => {
    setSeeding(true);
    try {
      const { data } = await axios.post(`${API}/library/templates/seed`);
      toast.success(`Seed OK — ${data.inserted} novos · ${data.updated} atualizados · ${data.total} totais`);
      await load();
    } catch (e) {
      toast.error("Falha no seed");
    } finally { setSeeding(false); }
  };

  const save = async () => {
    if (!draft?.name?.trim() || !draft?.content?.trim()) {
      toast.error("Nome e conteúdo são obrigatórios");
      return;
    }
    setSaving(true);
    try {
      const tags = (draft.tags || "").split(",").map((t) => t.trim()).filter(Boolean);
      const payload = {
        name: draft.name.trim(),
        kind: draft.kind || "email_body",
        category: (draft.category || "").trim() || null,
        language: draft.language || "pt",
        content: draft.content,
        tags,
      };
      if (draft.id) {
        await axios.put(`${API}/library/templates/${draft.id}`, payload);
        toast.success("Atualizado");
      } else {
        await axios.post(`${API}/library/templates`, payload);
        toast.success("Adicionado");
      }
      setDraft(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar");
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Excluir este template?")) return;
    try {
      await axios.delete(`${API}/library/templates/${id}`);
      toast.success("Excluído");
      await load();
    } catch (e) { toast.error("Falha ao excluir"); }
  };

  const filtered = items.filter((it) => {
    if (kindFilter && it.kind !== kindFilter) return false;
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (it.name || "").toLowerCase().includes(q)
        || (it.content || "").toLowerCase().includes(q)
        || (it.category || "").toLowerCase().includes(q)
        || (it.tags || []).join(" ").toLowerCase().includes(q);
  });

  const kindBadge = (k) => {
    const c = k === "email_subject" ? "bg-amber-100 text-amber-700"
            : k === "email_body"    ? "bg-indigo-100 text-indigo-700"
            : k === "image"         ? "bg-emerald-100 text-emerald-700"
            : "bg-[#F3F4F6] text-[#374151]";
    return c;
  };

  return (
    <MasterLayout title="Templates · Library">
      <div className="max-w-6xl mx-auto px-6 py-8" data-testid="master-templates">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#6B7280]">Master Console</p>
            <h1 className="font-display text-[28px] font-black text-[#111827] tracking-tight inline-flex items-center gap-2">
              <BookOpen className="w-6 h-6 text-[#7C3AED]" />
              Biblioteca de Templates
            </h1>
            <p className="text-[13px] text-[#6B7280] mt-1">
              Subjects · Bodies · Imagens. Consumidos por Sunbiz, Prospecting e Campanhas.
              Use variáveis <code className="bg-[#F3F4F6] px-1 py-0.5 rounded text-[11px]">{"{first_name}"}</code>, <code className="bg-[#F3F4F6] px-1 py-0.5 rounded text-[11px]">{"{company_name}"}</code>, <code className="bg-[#F3F4F6] px-1 py-0.5 rounded text-[11px]">{"{city}"}</code>, <code className="bg-[#F3F4F6] px-1 py-0.5 rounded text-[11px]">{"{sector}"}</code>, <code className="bg-[#F3F4F6] px-1 py-0.5 rounded text-[11px]">{"{sender_name}"}</code>.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={seed} disabled={seeding}
              data-testid="tpl-seed-btn"
              className="h-10 px-3 rounded-lg border border-[#E5E7EB] bg-white text-[12.5px] font-semibold inline-flex items-center gap-1.5 hover:bg-[#F9FAFB] disabled:opacity-50">
              {seeding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <BookOpen className="w-3.5 h-3.5" />}
              Importar Seeds
            </button>
            <button onClick={() => setDraft({ ...EMPTY })}
              data-testid="tpl-new-btn"
              className="h-10 px-4 rounded-lg bg-gradient-to-br from-[#7C3AED] to-[#4F46E5] text-white text-[13px] font-bold inline-flex items-center gap-2 hover:from-[#6D28D9] hover:to-[#4338CA]">
              <Plus className="w-4 h-4" /> Novo template
            </button>
          </div>
        </div>

        <div className="flex gap-2 mb-4 items-center">
          <div className="relative max-w-sm flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nome, conteúdo, categoria ou tag…"
              data-testid="tpl-search"
              className="w-full h-10 pl-9 pr-3 rounded-lg border border-[#E5E7EB] bg-white text-[13px] focus:outline-none focus:border-[#7C3AED]" />
          </div>
          <div className="inline-flex rounded-lg border border-[#E5E7EB] overflow-hidden bg-white">
            <button onClick={() => setKindFilter("")}
              className={`h-10 px-3 text-[12px] font-semibold border-r border-[#E5E7EB] ${kindFilter === "" ? "bg-[#EDE9FE] text-[#7C3AED]" : "bg-white text-[#6B7280]"}`}>
              Todos
            </button>
            {KINDS.map((k) => (
              <button key={k.id} onClick={() => setKindFilter(k.id)}
                data-testid={`tpl-filter-${k.id}`}
                className={`h-10 px-3 text-[12px] font-semibold border-r last:border-r-0 border-[#E5E7EB] inline-flex items-center gap-1.5 ${kindFilter === k.id ? "bg-[#EDE9FE] text-[#7C3AED]" : "bg-white text-[#6B7280] hover:bg-[#F9FAFB]"}`}>
                <k.icon className="w-3 h-3" /> {k.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="py-20 flex items-center justify-center text-[#6B7280]">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Carregando…
          </div>
        ) : filtered.length === 0 ? (
          <div className="al-card p-10 text-center text-[#6B7280]">
            <BookOpen className="w-10 h-10 mx-auto mb-3 text-[#D1D5DB]" />
            Nenhum template. Clique <strong>Importar Seeds</strong> para popular automaticamente.
          </div>
        ) : (
          <div className="grid gap-3" data-testid="tpl-list">
            {filtered.map((it) => (
              <article key={it.id}
                data-testid={`tpl-item-${it.id}`}
                className="al-card p-4 flex items-start gap-4 hover:border-[#7C3AED] transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-[14.5px] font-bold text-[#111827]">{it.name}</h3>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${kindBadge(it.kind)}`}>
                      {it.kind}
                    </span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#F9FAFB] border border-[#E5E7EB] text-[#6B7280] font-mono uppercase">{it.language}</span>
                    {it.category && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#EDE9FE] text-[#7C3AED] font-semibold">{it.category}</span>
                    )}
                  </div>
                  {it.kind === "image" ? (
                    <img src={it.content} alt={it.name}
                      className="mt-1 max-h-24 rounded border border-[#E5E7EB]"
                      onError={(e) => { e.target.style.display = "none"; }} />
                  ) : (
                    <p className="text-[12.5px] text-[#6B7280] leading-relaxed line-clamp-3 whitespace-pre-line">
                      {it.preview || it.content}
                    </p>
                  )}
                  {(it.tags || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {(it.tags || []).map((t) => (
                        <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-[#F9FAFB] border border-[#E5E7EB] text-[#6B7280]">#{t}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button onClick={() => setDraft({ ...it, tags: (it.tags || []).join(", ") })}
                    data-testid={`tpl-edit-${it.id}`}
                    className="w-8 h-8 rounded-lg border border-[#E5E7EB] hover:border-[#7C3AED] hover:text-[#7C3AED] flex items-center justify-center text-[#6B7280]"
                    title="Editar">
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => del(it.id)}
                    data-testid={`tpl-delete-${it.id}`}
                    className="w-8 h-8 rounded-lg border border-[#E5E7EB] hover:border-rose-500 hover:text-rose-500 flex items-center justify-center text-[#6B7280]"
                    title="Excluir">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>

      {draft && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-4"
          onClick={() => setDraft(null)}>
          <div onClick={(e) => e.stopPropagation()}
            data-testid="tpl-modal"
            className="al-card w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <header className="px-5 py-3 border-b border-[var(--al-border)] flex items-center justify-between">
              <h2 className="text-[14.5px] font-bold">{draft.id ? "Editar template" : "Novo template"}</h2>
              <button onClick={() => setDraft(null)} className="text-[#6B7280] hover:text-rose-500">
                <X className="w-4 h-4" />
              </button>
            </header>
            <div className="p-5 space-y-3 overflow-y-auto">
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="text-[11px] uppercase tracking-wider font-semibold text-[#6B7280]">Nome</label>
                  <input value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    placeholder="Ex.: Cold email curto · agências"
                    data-testid="tpl-name-input"
                    className="mt-1 w-full h-10 px-3 rounded-lg border border-[#E5E7EB] bg-white text-[13px] focus:outline-none focus:border-[#7C3AED]" />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider font-semibold text-[#6B7280]">Idioma</label>
                  <select value={draft.language || "pt"} onChange={(e) => setDraft({ ...draft, language: e.target.value })}
                    data-testid="tpl-lang-input"
                    className="mt-1 w-full h-10 px-2 rounded-lg border border-[#E5E7EB] bg-white text-[13px]">
                    {LANGS.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] uppercase tracking-wider font-semibold text-[#6B7280]">Tipo</label>
                  <select value={draft.kind || "email_body"} onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
                    data-testid="tpl-kind-input"
                    className="mt-1 w-full h-10 px-2 rounded-lg border border-[#E5E7EB] bg-white text-[13px]">
                    {KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider font-semibold text-[#6B7280]">Categoria</label>
                  <input value={draft.category || ""} onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                    placeholder="sunbiz / outreach / agency …"
                    data-testid="tpl-category-input"
                    className="mt-1 w-full h-10 px-3 rounded-lg border border-[#E5E7EB] bg-white text-[13px]" />
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider font-semibold text-[#6B7280]">
                  {draft.kind === "image" ? "URL da imagem" : "Conteúdo"}
                </label>
                <textarea value={draft.content || ""} onChange={(e) => setDraft({ ...draft, content: e.target.value })}
                  rows={draft.kind === "image" ? 2 : 10}
                  placeholder={draft.kind === "image" ? "https://…" : "Texto do template usando {first_name}, {company_name}, {city}…"}
                  data-testid="tpl-content-input"
                  className="mt-1 w-full p-3 rounded-lg border border-[#E5E7EB] bg-white text-[13px] leading-relaxed focus:outline-none focus:border-[#7C3AED]" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider font-semibold text-[#6B7280]">Tags (vírgula-separadas)</label>
                <input value={draft.tags || ""} onChange={(e) => setDraft({ ...draft, tags: e.target.value })}
                  placeholder="cold, follow-up, hero"
                  data-testid="tpl-tags-input"
                  className="mt-1 w-full h-10 px-3 rounded-lg border border-[#E5E7EB] bg-white text-[13px]" />
              </div>
            </div>
            <footer className="px-5 py-3 border-t border-[var(--al-border)] flex justify-end gap-2 bg-[#F9FAFB]">
              <button onClick={() => setDraft(null)} data-testid="tpl-cancel"
                className="h-9 px-3 rounded-lg border border-[#E5E7EB] bg-white text-[12.5px] font-semibold hover:bg-[#F3F4F6]">Cancelar</button>
              <button onClick={save} disabled={saving} data-testid="tpl-save"
                className="h-9 px-4 rounded-lg bg-gradient-to-br from-[#7C3AED] to-[#4F46E5] text-white text-[12.5px] font-bold inline-flex items-center gap-1.5 hover:from-[#6D28D9] hover:to-[#4338CA] disabled:opacity-50">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Salvar
              </button>
            </footer>
          </div>
        </div>
      )}
    </MasterLayout>
  );
}
